/**
 * WSB Sticky Expert Bar
 *
 * Dependencies: None (vanilla JS)
 *
 * Usage:
 * - Sticky bar appears when user scrolls past the element with [data-wsb-buy-box] attribute
 * - Add [data-wsb-buy-box] to your main buy box / product form
 * - Clicking "Talk to an Expert" opens the existing scta-modal on the page
 * - Clicking "Add to Cart" in sticky bar triggers the main form submission
 */

(function () {
  'use strict';

  // Configuration
  const CONFIG = {
    buyBoxSelector: '[data-wsb-buy-box]',
    stickyBarId: 'wsbStickyBar',
    visibleClass: 'is-visible',
    scrollThreshold: 0, // Show immediately when buy box scrolls out of view
  };

  // State
  let isBarVisible = false;
  let ticking = false;

  // DOM Elements
  let stickyBar;
  let buyBox;
  let expertBtn;
  let stickyAddToCartBtn;

  /**
   * Initialize the component
   */
  function init() {
    // Get DOM elements
    stickyBar = document.getElementById(CONFIG.stickyBarId);
    buyBox = document.querySelector(CONFIG.buyBoxSelector);
    expertBtn = document.getElementById('wsbExpertBtn');
    stickyAddToCartBtn = document.getElementById('wsbStickyAddToCart');

    if (!stickyBar) {
      console.warn('WSB Sticky Bar: Required elements not found');
      return;
    }

    // Bind events
    bindEvents();

    // Delay initial check to allow page layout to settle
    // This prevents the bar from briefly flashing on page load
    setTimeout(function() {
      checkStickyBarVisibility();
    }, 100);

    // Listen for variant changes to update price
    document.addEventListener('variant:change', onVariantChange);

    // Listen for configurator variant changes (bubbles up from section)
    document.addEventListener('on:variant:change', onVariantChange);
  }

  /**
   * Bind all event listeners
   */
  function bindEvents() {
    // Scroll handler for sticky bar
    window.addEventListener('scroll', onScroll, { passive: true });

    // Expert button click (opens existing modal)
    if (expertBtn) {
      expertBtn.addEventListener('click', openExpertModal);
    }

    // Sticky bar Add to Cart - trigger main form
    if (stickyAddToCartBtn) {
      stickyAddToCartBtn.addEventListener('click', onStickyAddToCart);
    }
  }

  /**
   * Scroll event handler (throttled with requestAnimationFrame)
   */
  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(function () {
        checkStickyBarVisibility();
        ticking = false;
      });
      ticking = true;
    }
  }

  /**
   * Check if sticky bar should be visible
   */
  function checkStickyBarVisibility() {
    if (!buyBox || !stickyBar) return;

    const buyBoxRect = buyBox.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;

    // Only show if buy box is above viewport AND user has actually scrolled
    // The scroll check prevents flash on initial page load due to layout timing
    const shouldShow = buyBoxRect.top < 0 && scrollY > 50;

    if (shouldShow && !isBarVisible) {
      showStickyBar();
    } else if (!shouldShow && isBarVisible) {
      hideStickyBar();
    }
  }

  /**
   * Show the sticky bar
   */
  function showStickyBar() {
    isBarVisible = true;
    stickyBar.classList.add(CONFIG.visibleClass);
  }

  /**
   * Hide the sticky bar
   */
  function hideStickyBar() {
    isBarVisible = false;
    stickyBar.classList.remove(CONFIG.visibleClass);
  }

  /**
   * Open the existing expert modal on the page
   * Looks for configurator-expert-modal or secondary-cta-modal
   */
  function openExpertModal() {
    // Try to find the existing modal (configurator modal or secondary CTA modal)
    const modal =
      document.getElementById('configurator-expert-modal') ||
      document.querySelector('.scta-modal');

    if (modal) {
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    } else {
      console.warn('WSB Sticky Bar: No expert modal found on page');
    }
  }

  /**
   * Handle sticky bar Add to Cart click
   * This triggers the main product form submission or configurator add-to-cart
   */
  function onStickyAddToCart() {
    const stickyBtn = document.getElementById('wsbStickyAddToCart');

    // Add loading state
    if (stickyBtn) {
      stickyBtn.classList.add('is-loading');
    }

    // Listen for cart update to remove loading state
    listenForCartUpdate();

    // Option 1: Click the configurator's Add to Cart button (for office templates)
    const configuratorAddToCart = document.querySelector(
      'product-configurator [data-add-to-cart]:not([disabled])'
    );
    if (configuratorAddToCart) {
      configuratorAddToCart.click();
      return;
    }

    // Option 2: Click the main Add to Cart button
    const mainAddToCartBtn = document.querySelector(
      '.add-to-cart-button[name="add"]'
    );
    if (mainAddToCartBtn && !mainAddToCartBtn.disabled) {
      mainAddToCartBtn.click();
      return;
    }

    // Option 3: Submit the main product form
    const productForm = document.querySelector(
      'form.js-product-form-main, form[action*="/cart/add"]'
    );
    if (productForm) {
      // Check if form has a submit button that's not disabled
      const submitBtn = productForm.querySelector('button[type="submit"]');
      if (submitBtn && !submitBtn.disabled) {
        submitBtn.click();
        return;
      }
    }

    // If nothing worked, remove loading state
    if (stickyBtn) {
      stickyBtn.classList.remove('is-loading');
    }

    console.warn(
      'WSB Sticky Bar: Could not find add to cart target or product is unavailable'
    );
  }

  /**
   * Listen for cart update events to remove loading state
   */
  function listenForCartUpdate() {
    const stickyBtn = document.getElementById('wsbStickyAddToCart');
    if (!stickyBtn) return;

    // Set a timeout fallback to remove loading state after 5 seconds
    const fallbackTimeout = setTimeout(function () {
      stickyBtn.classList.remove('is-loading');
    }, 5000);

    // Listen for various cart update events
    const removeLoading = function () {
      clearTimeout(fallbackTimeout);
      stickyBtn.classList.remove('is-loading');
      // Remove listeners after first trigger
      document.removeEventListener('cart:updated', removeLoading);
      document.removeEventListener('ajaxProduct:added', removeLoading);
      document.removeEventListener('cart:item-added', removeLoading);
    };

    document.addEventListener('cart:updated', removeLoading, { once: true });
    document.addEventListener('ajaxProduct:added', removeLoading, { once: true });
    document.addEventListener('cart:item-added', removeLoading, { once: true });

    // Also watch for cart drawer opening as a sign of success
    const cartDrawer = document.querySelector('cart-drawer, .cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.attributeName === 'open' || mutation.attributeName === 'class') {
            if (cartDrawer.hasAttribute('open') || cartDrawer.classList.contains('is-open')) {
              removeLoading();
              observer.disconnect();
            }
          }
        });
      });
      observer.observe(cartDrawer, { attributes: true });

      // Disconnect observer after timeout
      setTimeout(function () {
        observer.disconnect();
      }, 5000);
    }
  }

  /**
   * Handle variant change events to update sticky bar price and image
   */
  function onVariantChange(event) {
    if (!event.detail || !event.detail.variant) return;

    const variant = event.detail.variant;
    const priceEl = document.querySelector('.wsb-sticky-bar__price');
    const addToCartBtn = document.getElementById('wsbStickyAddToCart');
    const stickyImageEl = document.querySelector('.wsb-sticky-bar__image img');

    // Update price
    if (priceEl && variant.price) {
      priceEl.textContent = formatMoney(variant.price);
    }

    // Update image from the main gallery
    // Use the variant's featured media ID if available, or fall back to current visible image
    if (stickyImageEl) {
      const mediaId = variant.featured_media?.id;
      setTimeout(function () {
        updateStickyBarImage(mediaId);
      }, 150);
    }

    // Update button state based on availability
    if (addToCartBtn) {
      const btnText = addToCartBtn.querySelector('.wsb-sticky-bar__btn-text');
      if (variant.available) {
        addToCartBtn.disabled = false;
        if (btnText) btnText.textContent = 'Add to Cart';
      } else {
        addToCartBtn.disabled = true;
        if (btnText) btnText.textContent = 'Sold Out';
      }
    }
  }

  /**
   * Update sticky bar image from the main product gallery
   * @param {number} mediaId - Optional media ID from variant's featured_media
   */
  function updateStickyBarImage(mediaId) {
    const stickyImageEl = document.querySelector('.wsb-sticky-bar__image img');
    if (!stickyImageEl) return;

    let galleryImage = null;

    // First, try to find image by media ID if provided
    if (mediaId) {
      const mediaItem = document.querySelector('[data-media-id="' + mediaId + '"]');
      if (mediaItem) {
        galleryImage = mediaItem.querySelector('img.product-image') || mediaItem.querySelector('img');
      }
    }

    // Fall back to finding the active/visible image
    if (!galleryImage) {
      galleryImage =
        document.querySelector('.media-viewer__item.is-active img.product-image') ||
        document.querySelector('.media-viewer__item.is-current-variant img.product-image') ||
        document.querySelector('#gallery-viewer .is-active img') ||
        document.querySelector('#gallery-viewer li:first-child img.product-image') ||
        document.querySelector('.media-viewer img.product-image') ||
        document.querySelector('#product-media img.product-image');
    }

    if (galleryImage && galleryImage.src) {
      // Get a smaller version of the image for the sticky bar
      let newSrc = galleryImage.src;

      // If it's a Shopify CDN URL, request a smaller size
      if (newSrc.includes('cdn.shopify.com')) {
        // Replace width parameter or add it
        if (newSrc.includes('width=')) {
          newSrc = newSrc.replace(/width=\d+/, 'width=112');
        } else if (newSrc.includes('?')) {
          newSrc = newSrc + '&width=112';
        } else {
          newSrc = newSrc + '?width=112';
        }
      }

      stickyImageEl.src = newSrc;
      stickyImageEl.alt = galleryImage.alt || '';
    }
  }

  /**
   * Format money using Shopify's money format
   */
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(
        cents,
        window.theme?.moneyFormat || '${{amount}}'
      );
    }
    // Fallback formatting
    return '$' + (cents / 100).toFixed(2);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
